import React from 'react';
import { useLanguageStore } from '../../../stores/languageStore';
import type { MultilingualText } from '../survey.types';

/**
 * Props for the SurveySuccessPage component
 */
export interface SurveySuccessPageProps {
  /** Survey title */
  surveyTitle?: string;
  /** Optional multilingual HTML snippet from survey configuration */
  thankYouHtml?: MultilingualText | null;
}

/**
 * SurveySuccessPage Component
 *
 * Displays a success message after the user successfully submits the survey.
 * Shows a thank you message with a success icon in the user's selected language.
 *
 * @param props - Component props
 * @returns The success page UI
 *
 * @example
 * <SurveySuccessPage surveyTitle="Customer Feedback Survey" />
 */
export const SurveySuccessPage: React.FC<SurveySuccessPageProps> = ({
  thankYouHtml,
}) => {
  const languageCode = useLanguageStore((state) => state.code);

  const messages = {
    en: {
      title: 'Thank You!',
      message: 'Your response has been successfully submitted.',
      subtitle: 'We appreciate you taking the time to complete this survey.',
    },
    fr: {
      title: 'Merci!',
      message: 'Votre réponse a été soumise avec succès.',
      subtitle: 'Nous vous remercions d\'avoir pris le temps de répondre à cette enquête.',
    },
  };

  const currentMessages = messages[languageCode as keyof typeof messages] || messages.en;

  const additionalHtml = (() => {
    if (!thankYouHtml) return '';
    const lang = languageCode.toLowerCase();
    return thankYouHtml[lang] || thankYouHtml.en || '';
  })();

  return (
    <div className="survey-success-page">
      <div className="success-icon">
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="40" cy="40" r="40" fill="#10B981" />
          <path
            d="M25 40L35 50L55 30"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h1 className="success-title">{currentMessages.title}</h1>
      <p className="success-message">{currentMessages.message}</p>
      <p className="success-subtitle">{currentMessages.subtitle}</p>
      {additionalHtml && (
        <div
          className="success-additional-message"
          dangerouslySetInnerHTML={{ __html: additionalHtml }}
        />
      )}
    </div>
  );
};
